import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Alert, Typography, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthContext } from '../../../../context/AuthContext';
import { getGuestDisplayName } from '../../../../utils/guestDisplayUtils';
import FlashQuizWaiting from './FlashQuizWaiting';
import QuizQuestion from '../normalMode/QuizQuestion';
import FlashQuizResults from './FlashQuizResults';
import {
  PRACTICE_PARTICIPANT_ID,
  PRACTICE_PARTICIPANT_COUNT,
  createFakeLeaderboard,
  updateFakeLeaderboardAfterQuizlet,
  getUserRank,
  getFakeQuizletRank,
  buildPracticePushedQuizlets,
  buildPracticeQuizletStats,
  DEFAULT_PRACTICE_POINTS_CONFIG,
} from './flashPracticeUtils';

const PRACTICE_STEPS = {
  WAITING: 'waiting',
  QUESTION: 'question',
  RESULTS: 'results',
};

const QuizQuestionWrapper = ({ session, quizData, onComplete }) => {
  const flashConfig = useMemo(() => {
    const rawRapid = session?.rapid_config;
    const parsedRapid = typeof rawRapid === 'string'
      ? (() => { try { return JSON.parse(rawRapid); } catch { return {}; } })()
      : (rawRapid || {});

    return {
      delay_seconds: typeof parsedRapid.delay_seconds === 'number' ? parsedRapid.delay_seconds : 5,
      song_timer_seconds: typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60,
    };
  }, [session?.rapid_config]);

  const config = useMemo(() => ({
    difficulty_id: session?.quiz_config?.difficulty_id || 7,
    duration_minutes: 5,
    questions_per_song: session?.quiz_config?.questions_per_song || 5,
  }), [session?.quiz_config?.difficulty_id, session?.quiz_config?.questions_per_song]);

  return (
    <QuizQuestion
      config={config}
      quizData={quizData}
      currentQuizletIndex={0}
      onComplete={onComplete}
      mode="flash"
      flashConfig={flashConfig}
    />
  );
};

const FlashQuizPracticeMain = ({ onBack, onBackToWelcome, onPlayLive }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [practiceData, setPracticeData] = useState(null);
  const [session, setSession] = useState(null);
  const [participant, setParticipant] = useState(null);

  const [currentStep, setCurrentStep] = useState(PRACTICE_STEPS.WAITING);
  const [currentQuizletIndex, setCurrentQuizletIndex] = useState(-1);
  const [quizData, setQuizData] = useState(null);
  const [questionVersion, setQuestionVersion] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [finalPosition, setFinalPosition] = useState(0);
  const [finalLeaderboard, setFinalLeaderboard] = useState([]);
  const [quizletCompletionData, setQuizletCompletionData] = useState(null);
  const [lastPushedAt, setLastPushedAt] = useState(null);
  const [nextDeliveryTime, setNextDeliveryTime] = useState(null);

  const [fakeLeaderboard, setFakeLeaderboard] = useState([]);
  const [pushedQuizlets, setPushedQuizlets] = useState([]);
  const [quizletStats, setQuizletStats] = useState({});
  const [completedQuizletIndices, setCompletedQuizletIndices] = useState(new Set());

  const [, setPracticePointsConfig] = useState(DEFAULT_PRACTICE_POINTS_CONFIG);

  const pendingTimeoutRef = useRef(null);
  const advanceTimeoutRef = useRef(null);
  const hasCompletedQuizletRef = useRef(false);
  const currentQuizletIdRef = useRef(null);
  const practiceStartedRef = useRef(false);
  const currentStepRef = useRef(currentStep);

  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);

  const userDisplayName = useMemo(() => getGuestDisplayName(user) || 'You', [user]);

  const calculateMaxPoints = useCallback(async (difficultyId, questionsCount) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/lookup/difficulty/${difficultyId}`);
      if (response.ok) {
        const difficulty = await response.json();
        const basePoints = difficulty?.base_points || 1000;
        const quickTimeBonusMultiplier = difficulty?.quick_time_bonus_multiplier || 2;
        const maxPointsPerQuestion = basePoints * (1 + quickTimeBonusMultiplier);
        return Math.round(maxPointsPerQuestion * questionsCount);
      }
    } catch (error) {
      console.error('[FlashQuizPracticeMain] difficulty lookup failed:', error);
    }
    return Math.round(1000 * 3 * questionsCount);
  }, []);

  const calculateQuizletMaxPoints = useCallback(async (quizlet) => {
    const questions = quizlet?.questions || [];
    if (questions.length === 0) {
      return calculateMaxPoints(session?.quiz_config?.difficulty_id || 1, 5);
    }
    let total = 0;
    for (const question of questions) {
      total += await calculateMaxPoints(question.difficulty_id || session?.quiz_config?.difficulty_id || 1, 1);
    }
    return total;
  }, [calculateMaxPoints, session?.quiz_config?.difficulty_id]);

  const loadPracticeData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const res = await fetch(`${baseUrl}/api/live-quiz/flash/practice`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load practice quiz');
      }

      const mockSession = {
        session_id: 'practice',
        quiz_name: data.quiz_name || 'Insta Quiz Demo',
        status: 'active',
        quiz_type: 'flash',
        rapid_config: data.rapid_config,
        quiz_config: data.quiz_config,
      };

      const mockParticipant = {
        participant_id: PRACTICE_PARTICIPANT_ID,
        guest_name: userDisplayName,
        current_score: 0,
      };

      setPracticeData(data);
      setSession(mockSession);
      setParticipant(mockParticipant);
      setFakeLeaderboard(createFakeLeaderboard(userDisplayName, 0));
      setPushedQuizlets(buildPracticePushedQuizlets(data.quizlets || [], new Set(), null));

      const difficultyId = data.quiz_config?.difficulty_id || 1;
      try {
        const diffRes = await fetch(`${baseUrl}/api/lookup/difficulty/${difficultyId}`);
        if (diffRes.ok) {
          const difficulty = await diffRes.json();
          const basePoints = difficulty?.base_points || DEFAULT_PRACTICE_POINTS_CONFIG.basePoints;
          const speedBonusMultiplier = difficulty?.quick_time_bonus_multiplier
            ?? DEFAULT_PRACTICE_POINTS_CONFIG.speedBonusMultiplier;
          setPracticePointsConfig({
            basePoints,
            speedBonusMultiplier,
            maxPointsPerQuestion: Math.round(basePoints * (1 + speedBonusMultiplier)),
          });
        }
      } catch (_) {
        setPracticePointsConfig(DEFAULT_PRACTICE_POINTS_CONFIG);
      }
    } catch (error) {
      console.error('[FlashQuizPracticeMain] load error:', error);
      setLoadError(error.message || 'Unable to load demo mode');
    } finally {
      setLoading(false);
    }
  }, [userDisplayName]);

  useEffect(() => {
    loadPracticeData();
  }, [loadPracticeData]);

  const handleSessionEnd = useCallback(() => {
    setFinalLeaderboard(fakeLeaderboard);
    setFinalPosition(getUserRank(fakeLeaderboard) || 0);
    setCurrentStep(PRACTICE_STEPS.RESULTS);
  }, [fakeLeaderboard]);

  const pushQuizlet = useCallback((index, data = practiceData, sess = session) => {
    if (!data?.quizlets?.[index] || !sess) return;

    const quizlet = data.quizlets[index];
    const rawRapid = sess.rapid_config;
    const parsedRapid = typeof rawRapid === 'string'
      ? (() => { try { return JSON.parse(rawRapid); } catch { return {}; } })()
      : (rawRapid || {});
    const delaySeconds = typeof parsedRapid.delay_seconds === 'number' ? parsedRapid.delay_seconds : 5;
    const songTimerSeconds = typeof parsedRapid.song_timer_seconds === 'number' ? parsedRapid.song_timer_seconds : 60;

    const pushedAt = new Date().toISOString();
    const deliveryTime = new Date(Date.now() + delaySeconds * 1000).toISOString();
    const quizletId = `${quizlet.song?.songs_master_id || 'song'}_${pushedAt}`;
    currentQuizletIdRef.current = quizletId;
    hasCompletedQuizletRef.current = false;

    setCurrentQuizletIndex(index);
    setLastPushedAt(pushedAt);
    setNextDeliveryTime(deliveryTime);
    setQuizletCompletionData(null);
    setPushedQuizlets(buildPracticePushedQuizlets(data.quizlets, completedQuizletIndices, index));

    const formattedQuizData = {
      quiz_data: [{ song: quizlet.song, questions: quizlet.questions }],
      difficulty: data.difficulty || { base_time: songTimerSeconds },
    };

    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);

    pendingTimeoutRef.current = setTimeout(() => {
      setQuizData(formattedQuizData);
      setQuestionVersion((v) => v + 1);
      setCurrentStep(PRACTICE_STEPS.QUESTION);
      setNextDeliveryTime(null);
      pendingTimeoutRef.current = null;
    }, delaySeconds * 1000);
  }, [practiceData, session, completedQuizletIndices]);

  useEffect(() => {
    if (!practiceData || !session || practiceStartedRef.current) return;
    practiceStartedRef.current = true;
    pushQuizlet(0, practiceData, session);
  }, [practiceData, session, pushQuizlet]);

  const handleAnswerSubmitted = useCallback(async (quizletIndex, results) => {
    if (hasCompletedQuizletRef.current) return;
    hasCompletedQuizletRef.current = true;

    try {
      const answerData = {
        quizletIndex,
        results,
        timestamp: new Date().toISOString(),
        song: practiceData?.quizlets?.[currentQuizletIndex]?.song,
      };
      setUserAnswers((prev) => [...prev, answerData]);

      const newScore = totalScore + (results.totalScore || 0);
      setTotalScore(newScore);

      const updatedLeaderboard = updateFakeLeaderboardAfterQuizlet(fakeLeaderboard, results.totalScore || 0);
      setFakeLeaderboard(updatedLeaderboard);
      setParticipant((prev) => ({ ...prev, current_score: newScore }));

      const difficultyId = session?.quiz_config?.difficulty_id || 1;
      const actualQuestionsCount = results.totalQuestions || results.questionResults?.length || 5;
      const currentQuizlet = practiceData?.quizlets?.[currentQuizletIndex];
      const maxPoints = currentQuizlet?.questions?.length
        ? await calculateQuizletMaxPoints(currentQuizlet)
        : await calculateMaxPoints(difficultyId, actualQuestionsCount);
      const quizletRank = getFakeQuizletRank(results.totalScore || 0, maxPoints);
      const userRank = getUserRank(updatedLeaderboard);

      if (currentQuizletIdRef.current) {
        setQuizletStats((prev) => ({
          ...prev,
          ...buildPracticeQuizletStats(currentQuizletIdRef.current, results, quizletRank),
        }));
      }

      setQuizletCompletionData({
        answeredQuestions: results.questionResults?.length || results.totalQuestions || 0,
        totalQuestions: actualQuestionsCount,
        timeTaken: Math.round(results.timeTaken || 0),
        pointsScored: results.totalScore || 0,
        maxPoints,
        rank: userRank,
        quizletRank,
        quizletId: currentQuizletIdRef.current,
        timedOut: !!results.timedOut,
      });

      enqueueSnackbar(`+${results.totalScore} points! Total: ${newScore}`, { variant: 'success' });

      setCompletedQuizletIndices((prev) => {
        const next = new Set(prev);
        next.add(currentQuizletIndex);
        setPushedQuizlets(buildPracticePushedQuizlets(practiceData?.quizlets || [], next, null));
        return next;
      });

      if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);

      advanceTimeoutRef.current = setTimeout(() => {
        setCurrentStep(PRACTICE_STEPS.WAITING);
        setQuizData(null);
        setLastPushedAt(null);
        hasCompletedQuizletRef.current = false;
        currentQuizletIdRef.current = null;

        const nextIndex = currentQuizletIndex + 1;
        if (practiceData?.quizlets && nextIndex < practiceData.quizlets.length) {
          pushQuizlet(nextIndex);
        } else {
          setFinalLeaderboard(updatedLeaderboard);
          setFinalPosition(userRank || 0);
          setCurrentStep(PRACTICE_STEPS.RESULTS);
        }
        advanceTimeoutRef.current = null;
      }, 2500);
    } catch (error) {
      console.error('[FlashQuizPracticeMain] answer error:', error);
      hasCompletedQuizletRef.current = false;
    }
  }, [
    totalScore,
    fakeLeaderboard,
    session,
    practiceData,
    currentQuizletIndex,
    calculateQuizletMaxPoints,
    enqueueSnackbar,
    pushQuizlet,
  ]);

  useEffect(() => () => {
    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#121212' }}>
        <CircularProgress sx={{ color: '#9C27B0', mb: 2 }} />
        <Typography sx={{ color: '#fff' }}>Loading demo mode...</Typography>
      </Box>
    );
  }

  if (loadError || !session || !practiceData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#121212', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2, maxWidth: 480 }}>{loadError || 'Demo mode unavailable'}</Alert>
        <Typography component="button" onClick={onBack} sx={{ color: '#9C27B0', cursor: 'pointer', border: 'none', bgcolor: 'transparent', fontSize: '1rem' }}>
          Go Back
        </Typography>
      </Box>
    );
  }

  if (currentStep === PRACTICE_STEPS.RESULTS) {
    return (
      <FlashQuizResults
        totalScore={totalScore}
        position={finalPosition}
        leaderboard={finalLeaderboard}
        userAnswers={userAnswers}
        onBack={onBack}
        practiceMode
        onPlayLive={onPlayLive}
      />
    );
  }

  if (currentStep === PRACTICE_STEPS.QUESTION && quizData) {
    return (
      <QuizQuestionWrapper
        key={questionVersion}
        session={session}
        quizData={quizData}
        onComplete={handleAnswerSubmitted}
      />
    );
  }

  return (
    <FlashQuizWaiting
      session={session}
      participant={participant}
      onBack={onBackToWelcome || onBack}
      onQuestionReceived={() => {}}
      lastPushedAt={lastPushedAt}
      onSessionEnd={handleSessionEnd}
      quizletCompletionData={quizletCompletionData}
      stopNotice={null}
      onStopNoticeConsumed={() => {}}
      practiceMode
      practiceLeaderboard={fakeLeaderboard}
      practiceParticipantCount={PRACTICE_PARTICIPANT_COUNT}
      practicePushedQuizlets={pushedQuizlets}
      practiceQuizletStats={quizletStats}
      practiceNextDeliveryTime={nextDeliveryTime}
    />
  );
};

export default FlashQuizPracticeMain;
